import {Grid, Input, Stack, Text, Textarea, TextInput} from '@mantine/core';
import {useId} from '@mantine/hooks';
import React, {useState} from 'react';

import {MetadataGridContents} from 'analysis-tool/data-view/metadata-modal/ViewMetadataModal';
import FolderSelector from 'file-browser/FolderSelector';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import notifications from 'notifications';
import {ButtonStyle} from 'ui-library/buttons/Button';
import Modal from 'ui-library/modals/Modal';
import {readableTime} from 'util/DateUtil';
import {InputChangeEvent, TextAreaChangeEvent} from 'util/Types';

import {CanvasFolderArtifactFileStateGraphQL} from '../backbone/CanvasFolderArtifactGraphQL';
import {moveCanvasFolderArtifact, updateCanvasFolderArtifact} from '../network/CanvasFolderArtifactDAO';

export interface CanvasFolderArtifactMetadataModalProps {
  artifact: CanvasFolderArtifactFileStateGraphQL;
  onSaved: () => Promise<unknown>;
  onRequestClose: () => void;
  onRequestSubmit: () => void;
}

export default function CanvasFolderArtifactMetadataModal({
  artifact,
  onSaved,
  onRequestClose,
  onRequestSubmit,
}: CanvasFolderArtifactMetadataModalProps): JSX.Element {
  const [name, setName] = useState(artifact.name);
  const [description, setDescription] = useState(artifact.description ?? '');
  const [folderId, setFolderId] = useState(artifact.folder.id);
  const [isSaving, setIsSaving] = useState(false);
  const folderSelectorId = useId();
  const creatorName = isInaccessible(artifact.creator) ? null : artifact.creator.name;
  const isWritable = artifact.permissions.writable;
  const isMovable = artifact.permissions.movable;
  const canSave = isWritable || isMovable;
  const isNameBlank = name.trim().length === 0;

  const handleNameChange = (event: InputChangeEvent): void => {
    setName(event.currentTarget.value);
  };

  const handleDescriptionChange = (event: TextAreaChangeEvent): void => {
    setDescription(event.currentTarget.value);
  };

  const handleFolderChange = (event: {target: {value: string | null}}): void => {
    if (event.target.value == null) {
      return;
    }
    setFolderId(event.target.value);
  };

  const handleSave = async (): Promise<void> => {
    setIsSaving(true);
    try {
      let shouldClose = true;
      if (isWritable) {
        await updateCanvasFolderArtifact({
          id: artifact.id,
          name: name.trim(),
          description: description.length === 0 ? null : description,
        });
      }
      if (isMovable && folderId !== artifact.folder.id) {
        shouldClose = await moveCanvasFolderArtifact({
          id: artifact.id,
          fromFolderId: artifact.folder.id,
          toFolderId: folderId,
        });
      }
      if (shouldClose) {
        await onSaved();
        onRequestSubmit();
      }
      // Modal does not await onRequestSubmit, so rethrowing becomes an unhandled rejection.
      // eslint-disable-next-line benchlint/always-throw-from-catch -- the error is already shown; leave the modal open for retry
    } catch (error) {
      notifications.notifyError(error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      title="Metadata"
      buttons={
        canSave
          ? [
              {
                disabled: (isWritable && isNameBlank) || isSaving,
                style: ButtonStyle.PRIMARY,
                submit: true,
                text: isSaving ? 'Saving...' : 'Save',
              },
            ]
          : [{style: ButtonStyle.PRIMARY, submit: true, text: 'Done'}]
      }
      onRequestClose={onRequestClose}
      onRequestSubmit={canSave ? handleSave : onRequestSubmit}
      dataTestAttrs={{component: 'CanvasFolderArtifactMetadataModal', element: 'root'}}
    >
      <Stack spacing="xl">
        {canSave ? (
          <Stack spacing="md">
            <Grid gutter="md">
              <Grid.Col span={6}>
                {isWritable ? (
                  <TextInput label="Name" required={true} value={name} onChange={handleNameChange} />
                ) : (
                  <Stack spacing="xs">
                    <Text size="sm" weight={500}>
                      Name
                    </Text>
                    <Text>{artifact.name}</Text>
                  </Stack>
                )}
              </Grid.Col>
              <Grid.Col span={6}>
                <Input.Wrapper id={folderSelectorId} label="Folder">
                  <FolderField
                    folderId={folderId}
                    id={folderSelectorId}
                    readOnly={!isMovable}
                    onChange={handleFolderChange}
                  />
                </Input.Wrapper>
              </Grid.Col>
            </Grid>
            {isWritable ? (
              <Textarea
                label="Description"
                minRows={2}
                value={description}
                onChange={handleDescriptionChange}
              />
            ) : null}
          </Stack>
        ) : null}
        <MetadataGridContents
          gridCells={[
            ...(!isWritable
              ? [
                  ...(canSave ? [] : [{label: 'Name', component: artifact.name}]),
                  {label: 'Description', component: artifact.description ?? '—'},
                ]
              : []),
            {label: 'Created by', component: creatorName ?? '—'},
            {label: 'Created', component: readableTime(artifact.createdAt)},
            ...(!canSave
              ? [
                  {
                    label: 'Folder',
                    component: (
                      <FolderField folderId={folderId} readOnly={true} onChange={handleFolderChange} />
                    ),
                  },
                ]
              : []),
            {label: 'Modified', component: readableTime(artifact.modifiedAt)},
          ]}
        />
      </Stack>
    </Modal>
  );
}

function FolderField({
  folderId,
  id,
  readOnly,
  onChange,
}: {
  folderId: string;
  id?: string;
  readOnly: boolean;
  onChange: (event: {target: {value: string | null}}) => void;
}): JSX.Element {
  return (
    <FolderSelector
      checkExistingItemForDataModsMovement={true}
      enableRedesign={true}
      id={id}
      onChange={onChange}
      readOnly={readOnly}
      showInventory={true}
      showNotebook={true}
      showRegistries={false}
      unsetOnFetchFail={false}
      value={folderId}
    />
  );
}
